import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Button } from 'components/Button/Button';
import { SSO } from 'components/SSO/SSO';
import { TextInput } from 'components/TextInput/TextInput';
import useAppLanguage from 'hooks/useAppLanguage';
import usePageConfig from 'hooks/usePageConfig';
import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { SSOAuthProviderKey } from 'types/sso';
import { SSOMessages } from 'utils/constants';
import { isValidEmail } from 'utils/validation';
import { z } from 'zod';
import {
  useLoginOrSignupWithSsoMutation,
  useLoginWithEmailAndPasswordMutation,
} from '../api/auth-api';
import messages from './messages';
import { LoginUserState } from './types/LoginUserState';

export const Route = createFileRoute('/auth/login/')({
  component: LoginUser,
  validateSearch: zodValidator(
    z.object({
      redirect: z.string().optional(),
      email: z.string().optional(),
    }),
  ),
});

export default function LoginUser() {
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const language = useAppLanguage();
  const pageConfig = usePageConfig();
  const { redirect, email } = Route.useSearch();
  const [state, setState] = useState<LoginUserState>({
    username: email,
  });
  const [login, loginData] = useLoginWithEmailAndPasswordMutation();
  const [loginOrSignupWithSso, loginOrSignupWithSsoData] =
    useLoginOrSignupWithSsoMutation();

  const loginUserMessages = messages[language].loginUser;
  const supportEmail = pageConfig?.app?.supportEmail || 'support@altify.app';

  function isStateValid(value: LoginUserState): boolean {
    if (!value.username) return false;
    if (!value.password) return false;
    if (import.meta.env.VITE_ENV === 'production' && !value.recaptcha) {
      return false;
    }

    return isValidEmail(value.username);
  }

  // TODO: this is horrible and should be reworked with proper error codes
  const isAccountLocked = loginData.error?.message
    ?.toLowerCase()
    ?.includes('your account has been locked');
  const isWrongEmailOrPassword = loginData.error?.message
    ?.toLowerCase()
    ?.includes('password was not valid');

  const getEmailLoginError = () => {
    if (isWrongEmailOrPassword)
      return messages[language].loginUser.passwordErrorLabel;

    switch (loginData?.error?.code) {
      case SSOMessages.USER_SIGNED_UP_WITH_APPLE:
        return messages[language].loginUser.loginWithAppleError;
      case SSOMessages.USER_SIGNED_UP_WITH_GOOGLE:
        return messages[language].loginUser.loginWithGoogleError;
    }

    return loginData.error?.message ?? '';
  };

  function getSsoLoginError() {
    switch (loginOrSignupWithSsoData?.error?.code) {
      case SSOMessages.USER_SIGNED_UP_WITH_APPLE:
        return messages[language].loginUser.loginWithAppleError;
      case SSOMessages.USER_SIGNED_UP_WITH_GOOGLE:
        return messages[language].loginUser.loginWithGoogleError;
      case SSOMessages.USER_SIGNED_UP_WITH_PASSWORD:
        return messages[language].loginUser.loginWithPasswordError;
    }

    return loginOrSignupWithSsoData.error?.message ?? '';
  }

  const errorMessage = getEmailLoginError();
  const ssoErrorMessage = getSsoLoginError();

  async function onLoginUser(event: React.SyntheticEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!isStateValid(state)) return;
    loginOrSignupWithSsoData.reset();
    const loginRes = await login({
      email: state.username!,
      password: state.password!,
      recaptchaToken: state.recaptcha!,
    });

    if (!loginRes.error) {
      if (loginRes.data?.twoFactorRequired) {
        navigate({
          to: '/auth/login/2fa',
          search: { redirect },
        });
      } else {
        navigate({ to: redirect || '/' });
      }
    } else {
      (recaptchaRef.current as any)?.reset();
    }
  }

  async function onSsoLoginUser(provider: SSOAuthProviderKey) {
    loginData.reset();
    const result = await loginOrSignupWithSso(provider);
    if (!result.error) {
      if (result.data.twoFactorRequired) {
        navigate({
          to: '/auth/login/2fa',
          search: { redirect },
        });
      } else {
        navigate({ to: redirect || '/' });
      }
    } else {
      (recaptchaRef.current as any)?.reset();
    }
  }
  return (
    <div className="flex flex-col bg-background50 duration-500 ease-in-out transition-colors">
      <form onSubmit={onLoginUser}>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="font-semibold leading-9 mt-6 text-2.5x1 text-black text-center tracking-wide">
            {loginUserMessages.heading('Altify')}
          </h2>
          <p className="font-normal mt-3 text-center text-gray-900 text-lg tracking-wide mx-6 sm:mx-0">
            {loginUserMessages.subHeading}
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="m-6">
            <TextInput
              autofocus
              name="email"
              type="email"
              value={state.username}
              label={loginUserMessages.usernameLabel}
              warning={isWrongEmailOrPassword}
              onChange={value => {
                setState({ ...state, username: value as string });
              }}
            />
            <TextInput
              name="password"
              type="password"
              value={state.password}
              label={loginUserMessages.passwordLabel}
              warning={!!errorMessage}
              warningMessage={
                <div className="text-error text-sm">{errorMessage}</div>
              }
              onChange={value => {
                setState({ ...state, password: value as string });
              }}
            />

            <div className="mt-6 flex items-center justify-between flex-col">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={import.meta.env.VITE_GOOGLE_RECAPTCHA_KEY as string}
                onChange={value => {
                  setState({ ...state, recaptcha: (value as string) || '' });
                }}
              />
            </div>

            {isWrongEmailOrPassword && (
              <div className="mt-6">
                <p className="text-gray-900 text-center">
                  {loginUserMessages.supportErrorLabelBeforeEmail}&nbsp;
                  <a href={`mailto:${supportEmail}`} className="underline">
                    {supportEmail}
                  </a>
                  &nbsp;
                  {loginUserMessages.supportErrorLabelAfterEmail}
                </p>
              </div>
            )}

            {isAccountLocked && (
              <div className="mt-6">
                <p className="text-gray-900 text-center">
                  {loginUserMessages.supportLockLabelBeforeEmail}&nbsp;
                  <a href={`mailto:${supportEmail}`} className="underline">
                    {supportEmail}
                  </a>
                  &nbsp;
                  {loginUserMessages.supportLockLabelAfterEmail}
                </p>
              </div>
            )}

            <div className="text-center w-full mt-4">
              <span className="text-primary-button ">
                {loginUserMessages.forgotPasswordLink}
              </span>
              &nbsp;
              <Link
                to="/auth/reset-password/request"
                className="font-normal text-primary-button underline hover:no-underline">
                {loginUserMessages.resetPasswordHere}
              </Link>
            </div>

            <div className="mt-6">
              <span className="block w-full rounded-md">
                <Button
                  main
                  full
                  id="button:login:continue"
                  onClick={onLoginUser}
                  disabled={
                    !isStateValid(state) || loginOrSignupWithSsoData.isLoading
                  }
                  loading={loginData.isLoading}
                  type="submit">
                  {loginUserMessages.continueButton}
                </Button>
              </span>
            </div>

            <div className="flex flex-row align-middle justify-items-center items-center my-2">
              <hr className="flex-grow" />
              <div className="mx-2">OR</div>
              <hr className="flex-grow" />
            </div>

            <div className="text-error text-sm text-center">
              {ssoErrorMessage}
            </div>

            <SSO
              flow="login"
              loadingProvider={
                loginOrSignupWithSsoData.isLoading
                  ? loginOrSignupWithSsoData.originalArgs
                  : null
              }
              onButtonPress={provider => onSsoLoginUser(provider)}
            />

            <div className="mt-6 text-center w-full">
              <span className="text-center w-full">
                <span className="text-primary-button">
                  {loginUserMessages.needAccount}
                </span>
                &nbsp;{' '}
                <Link
                  to="/auth/signup"
                  search={{ email: state.username }}
                  className="font-normal text-right text-primary-button underline hover:no-underline">
                  {loginUserMessages.registerLink}
                </Link>
              </span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
